import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  ShieldCheck,
  Users,
  Workflow,
  BrainCircuit,
  ClipboardList,
  Gauge,
  LineChart,
} from "lucide-react";
import { useI18n } from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get("source");
    const campaign = params.get("campaign");
    const existing = JSON.parse(sessionStorage.getItem("dos_tracking") || "{}");
    const tracking = {
      source: source || existing.source || "direct",
      campaign: campaign || existing.campaign || "",
      referrer: existing.referrer || document.referrer || "",
    };
    sessionStorage.setItem("dos_tracking", JSON.stringify(tracking));
  }, [location.search]);

  const startSurvey = () => navigate("/survey");

  const audiences = [
    { icon: Users, label: t("aud_founders") },
    { icon: Gauge, label: t("aud_sme") },
    { icon: Workflow, label: t("aud_msme") },
    { icon: ClipboardList, label: t("aud_family") },
    { icon: LineChart, label: t("aud_md") },
  ];
  const reasons = [
    { icon: BrainCircuit, title: t("reason1_t"), body: t("reason1_b") },
    { icon: Workflow, title: t("reason2_t"), body: t("reason2_b") },
    { icon: LineChart, title: t("reason3_t"), body: t("reason3_b") },
  ];

  return (
    <div className="relative min-h-screen bg-[#0A0F1C] overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[520px] w-[520px] rounded-full bg-violet-600/20 blur-[150px]" />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 font-display text-lg font-bold text-white">
            D
          </div>
          <span className="font-display text-lg font-semibold tracking-tight text-white">
            Founder Survey
          </span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector variant="dark" />
          <button
            data-testid="nav-start-btn"
            onClick={startSurvey}
            className="hidden rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10 sm:block"
          >
            {t("start_survey")}
          </button>
        </div>
      </header>

      <section className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 pb-20 pt-10 lg:grid-cols-2 lg:pt-16">
        <div>
          <motion.div variants={fadeUp} initial="hidden" animate="show"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-blue-300">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
            {t("nav_badge")}
          </motion.div>

          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show"
            className="font-display text-4xl font-black leading-[1.08] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            {t("hero_1")}{" "}
            <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
              {t("hero_hl")}
            </span>{" "}
            {t("hero_2")}
          </motion.h1>

          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show"
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("hero_sub")}
          </motion.p>

          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show"
            className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <button data-testid="start-survey-btn" onClick={startSurvey}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-900/40 transition-transform hover:-translate-y-0.5">
              {t("start_survey")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Clock className="h-4 w-4 text-blue-400" />
              {t("est_time")}
            </div>
          </motion.div>
        </div>

        <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="relative">
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
            <img
              src="https://images.unsplash.com/photo-1774528205285-53a125a51983?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=650&fit=crop"
              alt="Confident SME founder in a modern office"
              className="h-[420px] w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -left-5 rounded-2xl border border-white/10 bg-[#0F1629]/90 px-5 py-4 backdrop-blur-xl">
            <p className="text-xs uppercase tracking-wide text-slate-400">Founder Pain Score</p>
            <p className="font-display text-2xl font-bold text-white">13 → 52</p>
          </div>
        </motion.div>
      </section>

      <section className="relative z-10 border-y border-white/5 bg-white/[0.02]">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {t("about_title")}
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                {t("about_body")}
              </p>
            </div>
            <div className="grid gap-4">
              {reasons.map((r, i) => (
                <motion.div key={r.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="flex gap-4 rounded-2xl border border-white/10 bg-[#0F1629] p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
                    <r.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{r.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{r.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
          {t("who_title")}
        </h2>
        <p className="mt-3 text-slate-400">{t("who_sub")}</p>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {audiences.map((a, i) => (
            <motion.div key={a.label} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="rounded-2xl border border-white/10 bg-[#0F1629] p-5 text-center" data-testid={`audience-${i}`}>
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300">
                <a.icon className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm font-medium text-slate-200">{a.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-24">
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1629] to-[#141d38] p-8 sm:p-12">
          <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" /> {t("privacy_badge")}
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                {t("privacy_title")}
              </h2>
              <p className="mt-3 text-slate-300">{t("privacy_body")}</p>
            </div>
            <button data-testid="cta-start-survey-btn" onClick={startSurvey}
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-[#0A0F1C] transition-transform hover:-translate-y-0.5">
              {t("start_survey")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
        <p className="mt-10 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} Founder Survey · {t("footer")}
        </p>
      </section>
    </div>
  );
}
